import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import clsx from 'clsx';
import React, { useEffect, useState, useRef } from 'react';

import AssignedTagsGrid from './AssignedTagsGrid';
import EditTagForm from './Elements/EditTagForm';
import TagInputCreate from './Elements/TagInputCreate';
import SelectionsGrid from './SelectionsGrid';
import { TagType } from './types';
import { TagModalProps } from './types/TagModalProps';

const TagModal: React.FC<TagModalProps> = ({
  modalStyle,
  onClose,
  assignedTags,
  onRemoveTag,
  onRestoreSuggestion,
  tagInput,
  setTagInput,
  suggestions,
  selections,
  onSelectSuggestion,
  onCreateTag,
  editTag = null,
  onEditTag,
  onSave,
  newTagColor,
  setNewTagColor,
  isTagReadOnly = false,
  onUnassignTag,
  setAssignedTags,
  onEditTagFormSubmit,
  onDeleteTag,
  disableCreateButton = false,
}) => {
  // State management
  const [showColorPicker, setShowColorPicker] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const isEditMode = !!editTag;

  // Sync tag input and color when edit mode changes
  useEffect(() => {
    if (editTag) {
      setTagInput(editTag.name);
      setNewTagColor(editTag.color);
    }
  }, [editTag, setTagInput, setNewTagColor]);

  // Handler for deleting a tag from SelectionsGrid
  const handleDeleteTag = (tag: TagType) => {
    if (onDeleteTag) onDeleteTag(tag);
  };

  // Handler for updating a tag from SelectionsGrid
  const handleEditTagFormSave = (label: string, color: string) => {
    if (!editTag) return;
    const updatedTag: TagType = {
      ...editTag,
      name: label,
      color,
    };
    // Call parent handler for confirm modal
    onEditTagFormSubmit?.(updatedTag);
  };

  const handleSave = () => {
    if (onSave) onSave();
    if (editTag) {
      const updatedTag: TagType = {
        ...editTag,
        name: tagInput,
        color: newTagColor,
      };
      setAssignedTags?.(assignedTags.map((t) => (t.id === editTag.id ? updatedTag : t)));
      onEditTagFormSubmit?.(updatedTag);
    }
    handleEditTagFormSave(tagInput, newTagColor);
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <div style={modalStyle} ref={modalRef} className={clsx('tw-z-[99]')}>
        <div
          className={clsx(
            'tw-bg-white',
            'tw-rounded-lg',
            'tw-shadow-lg',
            'tw-w-full',
            'tw-max-w-[320px]',
            'tw-p-4',
            'tw-relative'
          )}
        >
          {/* Modal Header */}
          <div className={clsx('tw-relative', 'tw-mb-4')}>
            <h3 className={clsx('tw-text-[14px]', 'tw-text-center', 'tw-pb-[8px]')}>
              {isEditMode ? 'Edit Tag' : 'Tags'}
            </h3>
            <button
              className={clsx(
                'tw-absolute',
                'tw-top-[-15px]',
                'tw-right-[-15px]',
                'tw-rounded-full',
                'tw-p-2',
                'tw-transition-colors'
              )}
              onClick={onClose}
              aria-label="Close modal"
            >
              ✕
            </button>
            <hr className={clsx('tw-mt-2', 'tw-mb-2', 'tw-border-gray-200')} />
          </div>
          {/* Edit mode: Only show input, preview, color picker, and Save button */}
          {isEditMode && editTag && !isTagReadOnly ? (
            <EditTagForm
              tagInput={tagInput}
              setTagInput={setTagInput}
              newTagColor={newTagColor}
              setNewTagColor={setNewTagColor}
              showColorPicker={showColorPicker}
              setShowColorPicker={setShowColorPicker}
              editTag={editTag}
              onSave={handleSave}
            />
          ) : (
            <div className={clsx('tw-space-y-4')}>
              {/* Assigned Tags Section */}
              <AssignedTagsGrid
                assignedTags={assignedTags}
                tagInput={tagInput}
                onRemoveTag={onRemoveTag}
                onUnassignTag={onUnassignTag}
                onRestoreSuggestion={onRestoreSuggestion}
                onEditTag={onEditTag}
                isTagReadOnly={isTagReadOnly}
                setAssignedTags={setAssignedTags}
              />

              {assignedTags.length > 0 && <hr className={clsx('tw-border-gray-200')} />}

              {/* Tag Creation Section */}
              {!isTagReadOnly && (
                <TagInputCreate
                  tagInput={tagInput}
                  setTagInput={setTagInput}
                  suggestions={suggestions}
                  onSelectSuggestion={onSelectSuggestion}
                  onCreateTag={onCreateTag}
                  newTagColor={newTagColor}
                  setNewTagColor={setNewTagColor}
                  showColorPicker={showColorPicker}
                  setShowColorPicker={setShowColorPicker}
                  disableCreateButton={disableCreateButton}
                />
              )}

              {/* Available Tags Section */}
              {!isTagReadOnly && (
                <SelectionsGrid
                  selections={selections}
                  onSelectSuggestion={onSelectSuggestion}
                  // Allow editing/deleting tags from selections
                  onEditTag={onEditTag}
                  onDeleteTag={handleDeleteTag}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};

export default TagModal;
